import { useState } from 'react';

import toast from 'components/partials/toast/toast';

import {
  DECISION_ACTIONS,
  NOTE_MAX_LENGTH,
} from 'containers/admin/clients/client/tabs-components/critical-populations/critical-populations.constants';
import type { DecisionActionKey } from 'containers/admin/clients/client/tabs-components/critical-populations/critical-populations.types';
import { notifyCaptureError } from 'containers/admin/clients/client/tabs-components/critical-populations/notify-capture-error';

import { useDecideCriticalPopulation } from 'hooks/api/use-critical-populations';

interface UseDecisionPanelArgs {
  clientId: number;
  id: number;
  action: DecisionActionKey;
  onDone: () => void;
}

interface UseDecisionPanelResult {
  reason: string;
  setReason: (reason: string) => void;
  canConfirm: boolean;
  isPending: boolean;
  handleConfirm: () => Promise<void>;
}

export const useDecisionPanel = ({
  clientId,
  id,
  action,
  onDone,
}: UseDecisionPanelArgs): UseDecisionPanelResult => {
  const [reason, setReason] = useState('');
  const { mutateAsync, isPending } = useDecideCriticalPopulation(clientId);
  const config = DECISION_ACTIONS[action];

  const trimmedReason = reason.trim();
  const canConfirm = !!trimmedReason && trimmedReason.length <= NOTE_MAX_LENGTH;

  const handleConfirm = async (): Promise<void> => {
    try {
      await mutateAsync({
        id,
        data: { status: config.targetStatus, note: trimmedReason },
      });
      toast.success({
        title: config.successTitle,
        message: config.successMessage,
      });
      onDone();
    } catch (error) {
      const isConflict = notifyCaptureError(error);

      if (isConflict) onDone();
    }
  };

  return {
    reason,
    setReason,
    canConfirm,
    isPending,
    handleConfirm,
  };
};
